/* eslint-disable no-return-assign */
/* eslint-disable no-sequences */
/* eslint-disable no-param-reassign */
import {
  FeatureFlags,
  Customer,
  CustomerAccount,
  PortfolioAllocation,
  PortfolioAllocationV2,
  StockCompanyWithWeight,
  WithdrawalAllocation
} from '@inyova/models';
import { cloneDeep } from 'lodash-es';

import { createSelector } from '@ngrx/store';
import {
  selectAccountFeatureFlag,
  selectCurrentAccount,
  selectCurrentInvestAccount,
  selectCustomer,
  selectCustomerAndCurrentInvestAccount,
  selectFeatureStrategy3A,
  selectStrategyFeature,
  selectStrategyFeatureCH,
  selectStrategyFeatureDE,
  selectStrategyFeatureUI,
  selectThirdPartyServices
} from '@app/app.reducers';

import {
  CompanyDetailData,
  CriteriumData,
  CustomerInvestmentData,
  RiskAnswer,
  RiskSurveyQuestion,
  Strategy3AState,
  StrategyCHState,
  StrategyDEState,
  StrategyState,
  StrategyStockAllocation,
  StrategyTopicsIds,
  StrategyUIState,
  TopicData
} from '@shared/models/Strategy';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';

/*
 * SELECTORS SHARED
 * */

// Strategy reducer selectors
export const selectStrategyWasChanged = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.wasStrategyChanged;
});
export const selectChangeStrategyDisabled = createSelector(selectStrategyFeature, (state: StrategyState) => state.changeStrategyDisabled);
export const selectIsEditModeEve2 = createSelector(
  selectCurrentAccount,
  selectStrategyFeature,
  (currentAccount: CustomerAccount, state: StrategyState) => {
    if (currentAccount.step_status < 5) {
      return true;
    }
    return state.isEditModeEve2;
  }
);
export const selectIsStrategyInitialised = createSelector(selectStrategyFeature, (state: StrategyState) => state.isStrategyInitialised);
export const selectTopicsData = createSelector(selectStrategyFeature, (state: StrategyState) => state.topicsData);

export const selectCustomerTopicsData = createSelector(selectStrategyFeature, (state: StrategyState) => state.customerTopicsData);

export const selectHandPrintFootPrintTopics = createSelector(
  selectTopicsData,
  (topics: TopicData[]): { handTopics: TopicData[]; footTopics: TopicData[] } => {
    const handTopics: TopicData[] = [];
    const footTopics: TopicData[] = [];

    for (const topic of topics) {
      switch (topic.print_type) {
        case 'Handprint':
          handTopics.push(topic);
          break;
        case 'Footprint':
          footTopics.push(topic);
          break;
        default:
          break;
      }
    }

    return { handTopics, footTopics };
  }
);

export const selectCustomerHandPrintFootPrintTopicsIds = createSelector(
  selectCustomerTopicsData,
  (topics: TopicData[]): { selectedHandTopics: number[]; selectedFootTopics: number[] } => {
    let selectedHandTopics: number[] = [];
    let selectedFootTopics: number[] = [];

    for (const topic of topics) {
      switch (topic.print_type) {
        case 'Handprint':
          selectedHandTopics.push(topic.id);
          break;
        case 'Footprint':
          selectedFootTopics.push(topic.id);
          break;
        default:
          break;
      }
    }

    selectedHandTopics = selectedHandTopics.sort((a, b) => a - b);
    selectedFootTopics = selectedFootTopics.sort((a, b) => a - b);

    return { selectedHandTopics, selectedFootTopics };
  }
);

export const selectCriteriaData = createSelector(selectStrategyFeature, (state: StrategyState) => state.criteriaData);

export const selectCustomerCriteriaData = createSelector(selectStrategyFeature, (state: StrategyState) => state.customerCriteriaData);

export const selectCustomerCriteriaDataIds = createSelector(selectCustomerCriteriaData, (state: CriteriumData[]) => {
  return state.map((criterium) => criterium.id).sort((a, b) => a - b);
});
export const selectCustomerCriteria = createSelector(selectCustomerCriteriaData, (state: CriteriumData[]) => {
  return state;
});

export const selectCompanyDetail = createSelector(selectStrategyFeature, (state: StrategyState) => state.companyDetailData);

export const selectCompanyList = createSelector(selectStrategyFeature, (state: StrategyState) => state.companyList);
export const selectPortfolioCompanyList = createSelector(selectStrategyFeature, (state: StrategyState) => state.portfolioCompanyList);
export const selectPortfolioCompanyLength = createSelector(selectStrategyFeature, selectAccountFeatureFlag(FeatureFlags.EVE2), (state, isEve2) =>
  isEve2 ? state.stocks.length : state.portfolioCompanyList.yova_stocks.length
);
export const selectStockCompanies = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.stocks.map(
    ({ company, position_weight, inyova_order }) =>
      ({
        ...company,
        position_weight,
        inyova_order
      }) as StockCompanyWithWeight
  );
});

// Broken portfolio selectors
export const selectBrokenPortfolio = createSelector(selectStrategyFeature, (state: StrategyState) => state.brokenPortfolio);
export const selectPortfolioWarningModal = createSelector(selectBrokenPortfolio, ({ showWarningModal }) => showWarningModal);
export const selectPortfolioUndoMessage = createSelector(selectBrokenPortfolio, ({ showUndoMessage }) => showUndoMessage);
export const selectPortfolioUndoLoading = createSelector(selectBrokenPortfolio, ({ doUndoCheck }) => doUndoCheck);

export const selectPortfolioWarningBox = createSelector(
  selectPortfolioCompanyList,
  selectPortfolioWarningModal,
  ({ broken_pf }, showWarningModal: boolean) => {
    return broken_pf && !showWarningModal;
  }
);
export const selectPortfolioCompanyListIDs = createSelector(selectPortfolioCompanyList, (state: StrategyState['portfolioCompanyList']) => {
  const { wishlist, blacklist } = state;
  const wishedCompanyIDs: number[] = wishlist.map((company) => company.id);
  const blockedCompanyIDs: number[] = blacklist.map((company) => company.id);

  return { wishedCompanyIDs, blockedCompanyIDs };
});

export const selectActiveCompanyID = createSelector(selectCompanyDetail, (state: CompanyDetailData) => {
  return state.company.id;
});

export const selectTab = createSelector(selectStrategyFeature, (state) => state.tabIndex);

export const selectTabIndex = createSelector(selectTab, selectCurrentAccount, (tabIndex: number, account: CustomerAccount) => {
  return { tabIndex, accountId: account.id };
});

export const selectGuaranteeVisibility = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.showGuarantee;
});
export const selectRiskSurveyFormData = createSelector(selectStrategyFeature, (state: StrategyState) => state.riskSurveyFormData);

export const selectCustomerInvestmentData = createSelector(
  selectStrategyFeature,
  selectCurrentInvestAccount,
  selectRiskSurveyFormData,
  selectStrategyWasChanged,
  selectAccountFeatureFlag(FeatureFlags.EVE2),
  (state, account, riskSurveyFormData, selectStrategyWasChanged, isEve2) =>
    isEve2 ? mapStrategyInfo(state, account, riskSurveyFormData, selectStrategyWasChanged.strategyWasChanged) : state.customerInvestmentData
);

const mapStrategyInfo = (
  state: StrategyState,
  account: CustomerAccount,
  riskSurveyFormData: {
    risk_answers: RiskAnswer[];
    selected_stock_split: number;
  },
  strategyWasChanged: boolean
): CustomerInvestmentData => {
  const isInvestedCustomer = account.step_status >= 5;

  // override stock split when changes have been made in edit mode
  const liveStockSplit = isInvestedCustomer
    ? strategyWasChanged && riskSurveyFormData?.selected_stock_split !== undefined
      ? riskSurveyFormData.selected_stock_split
      : state.strategyInfo?.live_stock_split
    : 0;

  const stockSplit = isInvestedCustomer
    ? strategyWasChanged && riskSurveyFormData?.selected_stock_split !== undefined
      ? riskSurveyFormData.selected_stock_split
      : state.strategyInfo?.live_stock_split
    : state.investmentPlan?.stock_split;

  return {
    ...state.customerInvestmentData,
    currency: account.currency,
    current_amount: account.current_amount,
    live_stock_split: liveStockSplit,
    stock_split: stockSplit,
    bonds: isInvestedCustomer ? state.strategyInfo?.bond_amount : 0,
    total: isInvestedCustomer ? state.strategyInfo?.total_invested_amount : state.investmentPlan?.initial_investment,
    monthly_investment: isInvestedCustomer ? state.strategyInfo?.monthly_investment : state.investmentPlan?.monthly_investment
  };
};

export const selectNotificationData = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.notification;
});

export const selectCustomerDiscardedWish = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.customerDiscardedWish;
});

export const selectDiscardedWishesDialog = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.dialogDiscardedWishes;
});

export const selectRiskStockSplit = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.stockSplit;
});

export const selectSearchCompanies = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.searchCompanies;
});

export const selectSearchCompaniesLoading = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return state.searchCompaniesLoading;
});

export const selectInterviewWidget = createSelector(
  selectCustomerAndCurrentInvestAccount,
  selectTabIndex,
  selectThirdPartyServices(),
  (entity, strategyTab, services: ThirdPartyServiceItem[]) => {
    if (strategyTab.tabIndex !== 5 || entity.currentAccountDetail?.step_status === 5) return false;

    const language = entity.customer.language.split('-')[0];
    return services.find((el) => el?.attributes.feature_key === `interview_widget_${language}`)?.attributes?.value || false;
  }
);

export const selectRiskName = createSelector(selectCustomerInvestmentData, (state: StrategyState['customerInvestmentData']) => {
  let riskName = '';
  switch (true) {
    case state.stock_split > 0.8:
      riskName = 'very_dynamic';
      break;
    case state.stock_split > 0.6:
      riskName = 'dynamic';
      break;
    case state.stock_split > 0.4:
      riskName = 'balanced';
      break;
    case state.stock_split > 0.2:
      riskName = 'conservative';
      break;
    case state.stock_split >= 0:
      riskName = 'very_conservative';
      break;
    default:
      riskName = 'balanced';
      break;
  }

  return { riskName };
});

export const selectPortfolioFemaleLeadership = createSelector(selectPortfolioCompanyList, (state: { female_leadership: number }) => {
  if (!state) return 0;
  const leadership = state.female_leadership / 100;
  return `${leadership.toFixed(1)}%`;
});

export const selectStockAllocation = createSelector(
  selectStrategyFeature,
  selectAccountFeatureFlag(FeatureFlags.EVE2),
  (state: StrategyState, isEve2: boolean) => {
    const colors = [
      'var(--chart-color-1)',
      'var(--chart-color-2)',
      'var(--chart-color-3)',
      'var(--chart-color-4)',
      'var(--chart-color-5)',
      'var(--chart-color-6)',
      'var(--chart-color-7)',
      'var(--chart-color-8)',
      'var(--chart-color-9)',
      'var(--chart-color-10)',
      'var(--chart-color-11)',
      'var(--chart-color-12)',
      'var(--chart-color-13)'
    ];

    if (isEve2) {
      const sectorWeights = Object.entries(state?.stockAllocationEve2?.sector_weights ?? {});
      const currencyWeights = Object.entries(state?.stockAllocationEve2?.currency_weights ?? {});

      if (!sectorWeights.length || !currencyWeights.length) {
        return {
          currency_percentages: [],
          sector_percentages: []
        };
      }

      const mapWeightsToGraphData = (weights) =>
        weights.map(([key, value], index) => ({
          id: index,
          key,
          value,
          color: colors[index]
        }));

      return {
        sector_percentages: mapWeightsToGraphData(sectorWeights),
        currency_percentages: mapWeightsToGraphData(currencyWeights)
      };
    }

    if (state?.stockAllocation?.sector_percentages?.length) {
      const sectorPercentage = [];
      const currencyPercentage = [];

      state.stockAllocation.currency_percentages.forEach((item, index) => {
        currencyPercentage.push({
          id: index,
          key: Object.keys(item)[0],
          value: item[Object.keys(item)[0]],
          color: colors[index]
        });
      });

      state.stockAllocation.sector_percentages.forEach((item, index) => {
        sectorPercentage.push({
          id: index,
          key: Object.keys(item)[0],
          value: item[Object.keys(item)[0]],
          color: colors[index]
        });
      });

      return {
        currency_percentages: currencyPercentage,
        sector_percentages: sectorPercentage
      };
    }

    return {
      currency_percentages: [],
      sector_percentages: []
    };
  }
);

export const selectStockAllocationDetails = createSelector(selectStrategyFeature, (state: StrategyState) => {
  return {
    currentTab: state.stockAllocation.currentTab,
    selectedIndex: state.stockAllocation.selectedIndex
  };
});

export const selectPortfolioAllocation = createSelector(selectStrategyFeature, (state: StrategyState) => state.portfolioAllocation);
export const selectPortfolioAllocationV2 = createSelector(selectStrategyFeature, (state: StrategyState) => state.portfolioAllocationV2);

export const selectWithdrawalAllocation = createSelector(
  selectAccountFeatureFlag(FeatureFlags.EVE2),
  selectPortfolioAllocation,
  selectPortfolioAllocationV2,
  selectCurrentAccount,
  (hasFeatureFlag: boolean, portfolioAllocation: PortfolioAllocation, portfolioAllocationV2: PortfolioAllocationV2, currentAccount) => {
    if (portfolioAllocation === null && portfolioAllocationV2 === null) return {} as unknown as WithdrawalAllocation;

    const minInvestment = currentAccount.minimum_investment;
    const totalWithCash = hasFeatureFlag ? portfolioAllocationV2.total_amount : portfolioAllocation.total_value;
    const cashValue = hasFeatureFlag ? portfolioAllocationV2.cash_amount : portfolioAllocation.cash_value;

    return {
      initial: {
        percentage: Number((minInvestment / totalWithCash).toFixed(2)),
        value: minInvestment
      },
      portfolio: {
        percentage: Number(((totalWithCash - minInvestment - cashValue) / totalWithCash).toFixed(2)),
        value: Number((totalWithCash - minInvestment - cashValue).toFixed(2))
      },
      cash: {
        percentage: Number((cashValue / totalWithCash).toFixed(2)),
        value: cashValue
      },
      totalAmount: totalWithCash
    };
  }
);

const selectReportingHasTechnicalIssue = createSelector(
  selectCustomerAndCurrentInvestAccount,
  selectThirdPartyServices('reporting_ch_3b'),
  selectThirdPartyServices('reporting_de_3b'),
  selectThirdPartyServices('reporting_ch_3a'),
  (
    { customer, currentAccountDetail },
    thirdPartyCh3b: ThirdPartyServiceItem,
    thirdPartyDe3b: ThirdPartyServiceItem,
    thirdPartyCh3a: ThirdPartyServiceItem
  ) => {
    if (customer.app_location === 'ch') {
      if (currentAccountDetail?.kind === '3b' || currentAccountDetail?.kind === 'kid') {
        return !thirdPartyCh3b.attributes.value;
      }
      return !thirdPartyCh3a.attributes.value;
    }

    if (customer.app_location === 'de') {
      if (currentAccountDetail?.kind === '3b') {
        return !thirdPartyDe3b.attributes.value;
      }
    }
    return true;
  }
);

export const selectAccountInvested = createSelector(
  selectStrategyFeature,
  selectReportingHasTechnicalIssue,
  (state: StrategyState, hasTechnicalIssue: boolean) => state.accountReportHasData && !hasTechnicalIssue
);

export const selectRandomStockCompanies = createSelector(
  selectAccountFeatureFlag(FeatureFlags.EVE2),
  selectStrategyFeature,
  selectStockAllocation,
  (hasFeatureFlag, state: StrategyState, selectStockAllocation: StrategyStockAllocation) => {
    let value: string;
    const selectedIndex = state.stockAllocation.selectedIndex;

    if (selectedIndex === undefined || selectedIndex === null) {
      return [];
    }

    if (state.stockAllocation.currentTab === 'sector') {
      value = selectStockAllocation.sector_percentages.find((item) => item.id === selectedIndex).key;
    } else {
      value = selectStockAllocation.currency_percentages.find((item) => item.id === selectedIndex).key;
    }

    const { wishlist, yova_stocks } = state.portfolioCompanyList;
    const wishlistNodiscarded = wishlist.filter((wish) => wish['is_included'] !== false);

    const stocks = hasFeatureFlag ? state.stocks.map((stock) => stock.company) : [...wishlistNodiscarded, ...yova_stocks];

    const filtered = stocks.filter((item) => {
      return item[state.stockAllocation.currentTab] === value;
    });

    return filtered.sort(() => 0.5 - Math.random()).slice(0, 3);
  }
);

export const selectInvestmentYear = createSelector(selectStrategyFeature, (state) => 20);

/*
 * SELECTORS 3A
 * */
export const select3ARiskIsInitialized = createSelector(selectFeatureStrategy3A, (state: Strategy3AState) => state.isInitialized);
export const select3ARiskQuestions = createSelector(selectFeatureStrategy3A, (state: Strategy3AState) => {
  return state.riskSurveyQuestions;
});
export const select3ARiskQuestionsSorted = createSelector(select3ARiskQuestions, (state: RiskSurveyQuestion[]) => {
  const willingnessQuestion = cloneDeep(state.filter((item) => item.subtype === 'willingness'));
  if (willingnessQuestion.length) {
    willingnessQuestion[0].survey_answer_options.unshift({
      details: 'Very conservative',
      id: 999,
      inyova_id: 99,
      option_text: 'Very conservative',
      score: -3,
      weight: 0
    });
  }
  return {
    investmentHorizon: state.filter((item) => item.subtype === 'investment_horizon'),
    expenses: state.filter((item) => item.subtype === 'expenses'),
    income: state.filter((item) => item.subtype === 'income'),
    riskTaking: state.filter((item) => item.subtype === 'risk-taking'),
    willingness: willingnessQuestion
  };
});
/*
 * SELECTORS CH
 * */
export const selectCHRiskIsInitialized = createSelector(selectStrategyFeatureCH, (state: StrategyCHState) => state.isInitialized);
export const selectRiskSurveyQuestions = createSelector(selectStrategyFeatureCH, (state) => state.riskSurveyQuestions);
export const selectCHRiskQuestions = createSelector(selectRiskSurveyQuestions, (questions) => questions.filter(({ inyova_id }) => inyova_id !== 6));
// We need to send to BE this question even we do not show it in interface for complainance reasons
export const selectCHHiddenRiskQuestion = createSelector(selectStrategyFeatureCH, (state: StrategyCHState) => {
  if (!state || !state.riskSurveyQuestions || !state.riskSurveyQuestions.length) return {};
  const question = state.riskSurveyQuestions.find((item) => item.inyova_id === 6);
  if (question && question.id) {
    const optionExperienced = question.survey_answer_options.find((item) => item.inyova_id === 27);
    const optionBeginner = question.survey_answer_options.find((item) => item.inyova_id === 26);
    return {
      questionID: question.id,
      optionExperiencedID: optionExperienced ? optionExperienced.id : 0,
      optionBeginnerID: optionBeginner ? optionBeginner.id : 0
    };
  }
  return {};
});
/*
 * SELECTORS UI
 * */
export const selectTopicsLoader = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.topicsButtonLoader;
});
export const selectInvestmentLoader = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.investmentButtonLoader;
});
export const selectRiskLoader = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.riskButtonLoader;
});
export const selectGenerateStrategyLoader = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.generateStrategyLoader;
});
export const selectGenerateProfileLoader = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.generateProfileLoader;
});
export const selectExpandedCompanyId = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.expandedCompanyId;
});
export const selectRiskToStrategyLoaderEve2 = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.riskToStrategyLoaderEve2;
});
export const selectIsSavingStrategyChangesEve2 = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.isSavingStrategyChangesEve2;
});
export const selectStrategyInfoLoaded = createSelector(selectStrategyFeatureUI, (state: StrategyUIState) => {
  return state.strategyInfoLoaded;
});
/**
 * COMPONENT VIEW SELECTORS
 **/
export const selectStrategyChanges = createSelector(
  selectChangeStrategyDisabled,
  selectStrategyWasChanged,
  (changeStrategyDisabled, strategyWasChanged) => ({
    changeStrategyDisabled,
    strategyWasChanged
  })
);

export const selectStrategyPersonaliseView = createSelector(
  selectCompanyList,
  selectNotificationData,
  selectSearchCompaniesLoading,
  selectPortfolioWarningBox,
  selectPortfolioUndoMessage,
  (companyList, notification, searchCompaniesLoading, showPortfolioWarningBox, showPortfolioUndoMessage) => ({
    companyList,
    notification,
    searchCompaniesLoading,
    showPortfolioWarningBox,
    showPortfolioUndoMessage
  })
);

export const selectStrategyTopicsView = createSelector(
  selectCustomer,
  selectCurrentAccount,
  selectHandPrintFootPrintTopics,
  selectCustomerHandPrintFootPrintTopicsIds,
  selectCriteriaData,
  selectCustomerCriteriaDataIds,
  (
    customer: Customer,
    currentAccount: CustomerAccount,
    { handTopics, footTopics }: { handTopics: TopicData[]; footTopics: TopicData[] },
    { selectedHandTopics, selectedFootTopics }: { selectedHandTopics: number[]; selectedFootTopics: number[] },
    criteria: CriteriumData[],
    selectedCriteria: number[]
  ) => {
    const originalIds: StrategyTopicsIds = {
      selectedHandTopics,
      selectedFootTopics,
      selectedCriteria
    };
    const selectedIds: StrategyTopicsIds = {
      selectedHandTopics: [...selectedHandTopics],
      selectedFootTopics: [...selectedFootTopics],
      selectedCriteria: [...selectedCriteria]
    };
    return {
      customer,
      currentAccount,
      handTopics,
      footTopics,
      criteria,
      originalIds,
      selectedIds
    };
  }
);
